// src/pages/CreateOrganization.js

import React, { useState, useEffect } from 'react';
import GlobalTable from '../components/GlobalTable';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';

const ORG_TYPES = [
    { value: 'pharmacy', label: 'Pharmacy' },
    { value: 'clinic', label: 'Clinic' },
];

const CreateOrganization = () => {
    const [organization, setOrganization] = useState({
        organization:'',
        org_type: 'pharmacy',
        admin_email: '',
        admin_password: '',
        admin_first_name: '',
        admin_last_name: '',
    });
    const [organizations, setOrganizations] = useState([]); // State to hold organization list
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(true);

    // Fetch organizations when component loads
    useEffect(() => {
        const fetchOrganizations = async () => {
            setLoading(true);
            try {
                const result = await axiosInstance.get('api/organizations');
                setOrganizations(result.data);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchOrganizations();
    }, []);

    // Handle organization creation
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const orgRes = await axiosInstance.post('api/organizations/', {
                organization: organization.organization,
                org_type: organization.org_type,
                admin_email: organization.admin_email,
                admin_password: organization.admin_password,
                admin_first_name: organization.admin_first_name,
                admin_last_name: organization.admin_last_name,
            });
            const newOrg = orgRes.data;

            setOrganization({
                organization: '',
                org_type: 'pharmacy',
                admin_email: '',
                admin_password: '',
                admin_first_name: '',
                admin_last_name: '',
            });
            setOrganizations([...organizations, newOrg]);
            handleApiSuccess(`Organization created: ${newOrg.organization}`);

        } catch (error) {
            handleApiError(error);
        }
    };

    // Handle row edit completion
    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;
        try {
            const updatedOrg = await axiosInstance.put(
                `api/organizations/${newData.id}/`,
                newData
            );
            const updatedOrganizations = [...organizations];
            updatedOrganizations[index] = updatedOrg.data;
            setOrganizations(updatedOrganizations);
            handleApiSuccess('Organization updated successfully!');
        } catch (error) {
            handleApiError(error);
        }
    };

    // Define table columns
    const columns = [
        { Header: 'Organization Name', accessor: 'organization', sortable: true, filtered:true },
        { Header: 'Type', accessor: 'org_type', sortable: true, filtered:true },
        { Header: 'Organization Id', accessor: 'id', readOnly: true },
        { Header: 'Created', accessor: 'created_at', readOnly: true, dataType:'date' }
    ];

    const headerAction = (
        <Button type="button" icon="pi pi-plus" label=" Add Organization" outlined onClick={() => setDialogVisible(true)} className="w-full sm:w-auto" />
    );
        
    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={organizations}
                    headerAction={headerAction}
                    tabletitle="Organization List"
                    onRowEditComplete={handleRowEditComplete}
                    loading={loading}
                />
                        
            </div>
            <Dialog
                header="Add Organization"
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '32rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
                <form id="dialogForm" onSubmit={handleSubmit}>
                <div style={{ borderRadius: '12px', padding: '1rem' }}>
                <div className="field" >
                        <label htmlFor="organization" className="font-bold">Organization Name:</label>
                        <InputText
                            name="organization"
                            id="organization"
                            value={organization.organization}
                            onChange={(e) => setOrganization(prev => ({ ...prev, organization: e.target.value }))}
                            placeholder='Enter organization name'
                            style={{ borderRadius: '6px'}}
                            required
                    />
                </div>
                <div className="field">
                        <label className="font-bold">Organization Type:</label>
                        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                            {ORG_TYPES.map((t) => (
                                <div key={t.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <RadioButton
                                        inputId={`org_type_${t.value}`}
                                        name="org_type"
                                        value={t.value}
                                        onChange={(e) => setOrganization(prev => ({ ...prev, org_type: e.value }))}
                                        checked={organization.org_type === t.value}
                                    />
                                    <label htmlFor={`org_type_${t.value}`}>{t.label}</label>
                                </div>
                            ))}
                        </div>
                </div>
                <div className="field" style={{ marginTop: '1rem', borderTop: '1px solid #e2eaf3', paddingTop: '1rem' }}>
                        <label className="font-bold">First Admin</label>
                        <p style={{ fontSize: 12.5, color: '#6b7c93', margin: '0.25rem 0 0.75rem' }}>
                            A new org has no staff yet — this creates its first admin so someone can log in and add
                            more staff from there. If this email already has a Goldfish login (e.g. they're already
                            a pharmacy admin), leave the password blank to attach that existing login instead of
                            creating a new one.
                        </p>
                </div>
                <div className="field">
                        <label htmlFor="admin_email" className="font-bold">Admin Email:</label>
                        <InputText
                            name="admin_email"
                            id="admin_email"
                            value={organization.admin_email}
                            onChange={(e) => setOrganization(prev => ({ ...prev, admin_email: e.target.value }))}
                            placeholder='admin@example.com'
                            style={{ borderRadius: '6px'}}
                            required
                    />
                </div>
                <div className="field">
                        <label htmlFor="admin_password" className="font-bold">Admin Password:</label>
                        <InputText
                            name="admin_password"
                            id="admin_password"
                            type="password"
                            value={organization.admin_password}
                            onChange={(e) => setOrganization(prev => ({ ...prev, admin_password: e.target.value }))}
                            placeholder='Leave blank to attach an existing login'
                            style={{ borderRadius: '6px'}}
                    />
                </div>
                <div className="field" style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                            <label htmlFor="admin_first_name" className="font-bold">First Name:</label>
                            <InputText
                                name="admin_first_name"
                                id="admin_first_name"
                                value={organization.admin_first_name}
                                onChange={(e) => setOrganization(prev => ({ ...prev, admin_first_name: e.target.value }))}
                                style={{ borderRadius: '6px'}}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label htmlFor="admin_last_name" className="font-bold">Last Name:</label>
                            <InputText
                                name="admin_last_name"
                                id="admin_last_name"
                                value={organization.admin_last_name}
                                onChange={(e) => setOrganization(prev => ({ ...prev, admin_last_name: e.target.value }))}
                                style={{ borderRadius: '6px'}}
                            />
                        </div>
                </div>
                </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Save" icon="pi pi-check" form="dialogForm" type='submit' style={{borderRadius:"6px", maxWidth: "120px"}} />
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
                    </div> 
                </form>
            </Dialog>
        </div>
    );
};

export default CreateOrganization;
